export interface AssistantPageContext {
  route: string;
  module: string;
  page: string;
  hierarchy: string[];
  purpose: string;
}

const pages: Array<{ match: RegExp; module: string; page: string; purpose: string }> = [
  { match: /^\/dashboard$/, module: '工作总览', page: '工作总览', purpose: '查看项目、待办、审批和风险概况' },
  { match: /^\/projects$/, module: '项目中心', page: '项目列表', purpose: '查询、新建和进入招标或监理项目' },
  { match: /^\/projects\/[^/]+$/, module: '项目中心', page: '项目工作区', purpose: '处理项目概况、业务记录、文件、协同与审批' },
  { match: /^\/workbench$/, module: '协同办公', page: '我的工作台', purpose: '处理本人任务、审批和通知' },
  { match: /^\/calculators$/, module: '计算中心', page: '工程计算', purpose: '使用招标、监理和工程公式并保留计算记录' },
  { match: /^\/ai$/, module: 'AI智能应用', page: 'AI业务助手', purpose: '使用专门业务场景执行AI分析' },
  { match: /^\/ai-jobs$/, module: 'AI智能应用', page: 'AI任务记录', purpose: '查看AI任务输入、状态和输出' },
  { match: /^\/settings$/, module: '系统管理', page: '系统设置', purpose: '配置模型供应商和Prompt模板' },
  { match: /^\/menu-management$/, module: '系统管理', page: '菜单管理', purpose: '维护菜单层级、路由、权限和外部链接' },
  { match: /^\/users$/, module: '系统管理', page: '组织与权限', purpose: '维护用户、部门、角色、功能权限和数据范围' },
  { match: /^\/audit$/, module: '系统管理', page: '审计日志', purpose: '追踪系统操作与数据变更' },
  { match: /^\/external\//, module: '外部应用', page: '外部页面', purpose: '使用菜单嵌入的外部系统' },
];

export function resolveAssistantPage(pathname: string, search = ''): AssistantPageContext {
  const found = pages.find((item) => item.match.test(pathname)) ?? { module: '系统', page: '当前页面', purpose: '协助理解和操作当前功能' };
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  const tabLabels: Record<string, string> = { users: '用户管理', departments: '部门管理', roles: '角色与数据范围', permissions: '权限字典' };
  const page = pathname === '/users' && tabLabels[tab ?? ''] ? tabLabels[tab!] : found.page;
  return { route: `${pathname}${search}`, module: found.module, page, hierarchy: found.module === page ? [found.module] : [found.module, page], purpose: found.purpose };
}

const uniqueText = (values: string[]) => [...new Set(values.map((item) => item.replace(/\s+/g, ' ').trim()).filter(Boolean))];

/** 只收集用户当前可见页面的结构摘要，不采集密码、密钥和隐藏字段。 */
export function captureVisiblePageContext(root: ParentNode = document) {
  const scope = root.querySelector('main') ?? root;
  const headings = uniqueText([...scope.querySelectorAll('h1,h2,h3,h4')].map((item) => item.textContent ?? '')).slice(0, 20);
  const actions = uniqueText([...scope.querySelectorAll('button')].filter((item) => !(item as HTMLButtonElement).disabled).map((item) => item.textContent ?? '')).slice(0, 30);
  const activeTabs = uniqueText([...scope.querySelectorAll('[role="tab"][aria-selected="true"]')].map((item) => item.textContent ?? '')).slice(0, 8);
  const fields = [...scope.querySelectorAll('.ant-form-item')].flatMap((item) => {
    const control = item.querySelector('input,textarea') as HTMLInputElement | HTMLTextAreaElement | null;
    if (!control || control.type === 'password' || control.type === 'hidden') return [];
    const label = item.querySelector('label')?.textContent?.replace(/\*/g, '').trim() || control.getAttribute('aria-label') || control.name || control.id;
    const name = control.name || control.id;
    if (!label || !name) return [];
    return [{ name, label, value: control.value?.slice(0, 500) ?? '', editable: !control.disabled && !control.readOnly }];
  }).slice(0, 40);
  return { headings, activeTabs, actions, fields };
}

/** 将AI建议写入当前可见的文本/数字字段；只填值，不触发表单提交。 */
export function applyAssistantFields(fields: Record<string, unknown>, root: ParentNode = document) {
  const applied: string[] = [];
  const skipped: string[] = [];
  for (const [name, raw] of Object.entries(fields)) {
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(name) : name.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
    const control = root.querySelector(`[name="${escaped}"],#${escaped}`) as HTMLInputElement | HTMLTextAreaElement | null;
    if (!control || control.disabled || control.readOnly || control.type === 'password' || control.type === 'hidden') { skipped.push(name); continue; }
    const value = String(raw ?? '');
    const prototype = control instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
    setter?.call(control, value);
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
    applied.push(name);
  }
  return { applied, skipped };
}
